import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Alert,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Paper,
  Chip,
  Switch,
  FormControlLabel,
  MenuItem,
  Autocomplete
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { addDocument, getCollection, updateDocument, deleteDocument } from '../services/firestoreService';

const LeadDiagnosesManager = () => {
  const [diagnosisCode, setDiagnosisCode] = useState('');
  const [goalName, setGoalName] = useState('');
  const [needName, setNeedName] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [mappings, setMappings] = useState([]);
  const [diagnoses, setDiagnoses] = useState([]);
  const [goals, setGoals] = useState([]);
  const [needs, setNeeds] = useState([]);

  const diagnosesMap = useMemo(() => {
    return diagnoses.reduce((acc, d) => {
      if (d.code) acc[d.code] = d;
      return acc;
    }, {});
  }, [diagnoses]);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [editingId, setEditingId] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [mData, dData, gData, nData] = await Promise.all([
        getCollection('lead-diagnoses'),
        getCollection('diagnoses'),
        getCollection('goals'),
        getCollection('needs')
      ]);
      setMappings(mData || []);
      setDiagnoses(dData || []);
      setGoals(gData || []);
      setNeeds(nData || []);
    } catch (error) {
      console.error('Error cargando datos:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const selected = diagnosesMap[diagnosisCode] || {};
      const payload = {
        diagnosis: (selected.diagnosis || '').trim(),
        diagnosisCode: diagnosisCode.trim(),
        goalName: goalName || '',
        needName: needName || '',
        isActive,
        updatedAt: new Date()
      };
      if (editingId) {
        await updateDocument('lead-diagnoses', editingId, payload);
        setSnackbar({ open: true, message: 'Mapeo actualizado', severity: 'success' });
      } else {
        await addDocument('lead-diagnoses', { ...payload, createdAt: new Date() });
        setSnackbar({ open: true, message: 'Mapeo agregado', severity: 'success' });
      }
      setDiagnosisCode('');
      setGoalName('');
      setNeedName('');
      setIsActive(true);
      setEditingId(null);
      loadData();
    } catch (error) {
      console.error('Error guardando mapeo:', error);
      setSnackbar({ open: true, message: 'Error al guardar', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (m) => {
    setEditingId(m.id);
    setDiagnosisCode(m.diagnosisCode || '');
    setGoalName(m.goalName || '');
    setNeedName(m.needName || '');
    setIsActive(m.isActive !== false);
  };

  const handleCancel = () => {
    setEditingId(null);
    setDiagnosisCode('');
    setGoalName('');
    setNeedName('');
    setIsActive(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este mapeo?')) return;
    try {
      await deleteDocument('lead-diagnoses', id);
      setSnackbar({ open: true, message: 'Mapeo eliminado', severity: 'success' });
      loadData();
    } catch (error) {
      console.error('Error eliminando mapeo:', error);
      setSnackbar({ open: true, message: 'Error al eliminar', severity: 'error' });
    }
  };

  const handleToggle = async (m) => {
    try {
      await updateDocument('lead-diagnoses', m.id, { isActive: !m.isActive, updatedAt: new Date() });
      loadData();
    } catch (error) {
      console.error('Error actualizando mapeo:', error);
    }
  };

  const needsForGoal = useMemo(() => {
    if (!goalName) return needs.filter((n) => n.isActive !== false);
    return needs.filter((n) => n.goalName === goalName && n.isActive !== false);
  }, [goalName, needs]);

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Diagnósticos que generan leads automáticos
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Cuando una ficha clínica contenga uno de estos diagnósticos, se creará un lead con la meta y necesidad asociadas.
      </Typography>

      <Paper sx={{ p: 3, mt: 2, bgcolor: 'background.default' }}>
        <Box component="form" onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Autocomplete
                fullWidth
                options={diagnoses
                  .filter((d) => d.isActive !== false)
                  .sort((a, b) => (a.diagnosis || '').localeCompare(b.diagnosis || ''))}
                value={diagnoses.find((d) => d.code === diagnosisCode) || null}
                onChange={(_, newValue) => setDiagnosisCode(newValue?.code || '')}
                getOptionLabel={(option) => `${option.diagnosis || ''} (${option.code || ''})`}
                isOptionEqualToValue={(option, val) => option.code === val.code}
                filterOptions={(options, params) => {
                  const input = params.inputValue.toLowerCase();
                  return options.filter(
                    (o) =>
                      (o.diagnosis || '').toLowerCase().includes(input) ||
                      (o.code || '').toLowerCase().includes(input)
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Diagnóstico"
                    required
                    helperText="Busca por nombre o código"
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <TextField
                fullWidth
                select
                label="Meta"
                value={goalName}
                onChange={(e) => { setGoalName(e.target.value); setNeedName(''); }}
                helperText="Vincula a una meta"
              >
                <MenuItem value="">Sin meta</MenuItem>
                {goals
                  .filter((g) => g.isActive !== false)
                  .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                  .map((g) => (
                    <MenuItem key={g.id} value={g.name}>{g.name}</MenuItem>
                  ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <TextField
                fullWidth
                select
                label="Necesidad"
                value={needName}
                onChange={(e) => setNeedName(e.target.value)}
                helperText="Vincula a una necesidad"
              >
                <MenuItem value="">Sin necesidad</MenuItem>
                {needsForGoal
                  .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                  .map((n) => (
                    <MenuItem key={n.id} value={n.name}>{n.name}</MenuItem>
                  ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 2 }}>
              <FormControlLabel
                control={<Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />}
                label="Activo"
              />
            </Grid>
            <Grid size={12}>
              <Button type="submit" variant="contained" disabled={loading} sx={{ mr: 1 }}>
                {editingId ? 'Actualizar' : 'Agregar'}
              </Button>
              {editingId && (
                <Button variant="outlined" onClick={handleCancel}>Cancelar</Button>
              )}
            </Grid>
          </Grid>
        </Box>
      </Paper>

      <Box sx={{ mt: 4 }}>
        <Typography variant="h6" gutterBottom>
          Mapeos registrados ({mappings.length})
        </Typography>
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Diagnóstico</TableCell>
                <TableCell>Meta</TableCell>
                <TableCell>Necesidad</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {mappings.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    No hay mapeos registrados
                  </TableCell>
                </TableRow>
              ) : (
                mappings.map((m) => (
                  <TableRow key={m.id}>
                    <TableCell>
                      {m.diagnosis}
                      <br />
                      <Typography component="span" color="text.secondary" sx={{ fontSize: '0.8em' }}>
                        {m.diagnosisCode}
                      </Typography>
                    </TableCell>
                    <TableCell>{m.goalName || '—'}</TableCell>
                    <TableCell>{m.needName || '—'}</TableCell>
                    <TableCell>
                      <Switch
                        checked={m.isActive !== false}
                        onChange={() => handleToggle(m)}
                        size="small"
                      />
                      <Chip
                        label={m.isActive !== false ? 'Activo' : 'Inactivo'}
                        color={m.isActive !== false ? 'success' : 'default'}
                        size="small"
                        sx={{ ml: 1 }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" color="primary" onClick={() => handleEdit(m)}>
                        <EditIcon />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDelete(m.id)}>
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default LeadDiagnosesManager;
